import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, Tag } from 'lucide-react';
import { useStore } from '../admin/context/ProductStore';
import './Cart.css';

export default function Cart({ cart, setCart }) {
  const { getFullUrl } = useStore();
  const [coupon, setCoupon] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponError, setCouponError] = useState('');

  const updateQty = (id, delta) => {
    setCart(prev =>
      prev.map(item =>
        item.id === id ? { ...item, qty: Math.max(1, (item.qty || 1) + delta) } : item
      )
    );
  };

  const removeItem = (id) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const subtotal = cart.reduce((sum, item) => sum + item.price * (item.qty || 1), 0);
  const shipping = subtotal >= 599 ? 0 : 49;
  const discount = couponApplied ? Math.round(subtotal * 0.1) : 0;
  const total = subtotal + shipping - discount;

  const applyCoupon = () => {
    if (coupon.trim().toUpperCase() === 'KOA10') {
      setCouponApplied(true);
      setCouponError('');
    } else {
      setCouponError('Invalid coupon code. Try KOA10');
      setCouponApplied(false);
    }
  };

  if (cart.length === 0) {
    return (
      <main className="cart-empty container">
        <div className="empty-state">
          <ShoppingBag size={64} className="empty-icon" />
          <h2>Your cart is empty</h2>
          <p>Looks like you haven't added anything yet. Explore our premium collection!</p>
          <Link to="/shop" className="btn-primary">
            Start Shopping <ArrowRight size={16} />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <div className="cart-hero">
        <div className="container">
          <h1 className="cart-title">Your Cart</h1>
          <p className="cart-count">Review your premium selection ({cart.length} item{cart.length > 1 ? 's' : ''})</p>
        </div>
      </div>

      <div className="container cart-layout">
        {/* Items */}
        <div className="cart-items-wrap">
          <div className="cart-items">
            {cart.map(item => (
              <div key={item.id} className="cart-item">
                <div className="cart-item-img">
                  <img
                    src={getFullUrl(item.image)}
                    alt={item.name}
                    onError={(e) => {
                      e.target.src = `https://via.placeholder.com/120x120/fdf8f0/7a4e2d?text=${encodeURIComponent(item.name[0])}`;
                    }}
                  />
                </div>
                <div className="cart-item-info">
                  <Link to={`/product/${item.slug}`} className="cart-item-name">{item.name}</Link>
                  <p className="cart-item-weight">{item.weight}</p>
                  <div className="cart-item-actions">
                    <div className="qty-control">
                      <button className="qty-btn" onClick={() => updateQty(item.id, -1)} aria-label="Decrease quantity"><Minus size={14} /></button>
                      <span className="qty-val">{item.qty || 1}</span>
                      <button className="qty-btn" onClick={() => updateQty(item.id, 1)} aria-label="Increase quantity"><Plus size={14} /></button>
                    </div>
                    <button className="remove-btn" onClick={() => removeItem(item.id)}>
                      <Trash2 size={16} /> Remove
                    </button>
                  </div>
                </div>
                <div className="cart-item-price">
                  <span className="item-total">₹{item.price * (item.qty || 1)}</span>
                  <span className="item-unit">₹{item.price} each</span>
                </div>
              </div>
            ))}
          </div>

          {/* Coupon */}
          <div className="coupon-section">
            <div className="coupon-form">
              <Tag size={18} className="coupon-icon" />
              <input
                type="text"
                placeholder="Enter coupon code (try KOA10)"
                value={coupon}
                onChange={e => setCoupon(e.target.value)}
                className="coupon-input"
              />
              <button className="coupon-btn" onClick={applyCoupon}>Apply</button>
            </div>
            {couponApplied && <p className="coupon-success">✓ Coupon applied! 10% discount added.</p>}
            {couponError && <p className="coupon-error">{couponError}</p>}
          </div>
        </div>

        {/* Summary */}
        <div className="cart-summary">
          <h3 className="summary-title">Order Summary</h3>
          
          {/* Shipping Progress */}
          <div className="shipping-progress">
            <div className="progress-label">
              <span>{shipping === 0 ? 'Free Shipping unlocked!' : `Add ₹${599 - subtotal} for FREE shipping`}</span>
              <span>{Math.min(100, Math.round((subtotal / 599) * 100))}%</span>
            </div>
            <div className="progress-bar-bg">
              <div 
                className="progress-bar-fill" 
                style={{ width: `${Math.min(100, (subtotal / 599) * 100)}%` }}
              />
            </div>
          </div>

          <div className="summary-rows">
            <div className="summary-row">
              <span>Subtotal</span>
              <span>₹{subtotal}</span>
            </div>
            <div className="summary-row">
              <span>Estimated Shipping</span>
              <span className={shipping === 0 ? 'free-tag' : ''}>{shipping === 0 ? 'FREE' : `₹${shipping}`}</span>
            </div>
            {couponApplied && (
              <div className="summary-row discount-row">
                <span>Discount (KOA10)</span>
                <span>−₹{discount}</span>
              </div>
            )}
          </div>

          <div className="summary-total">
            <span>Total</span>
            <span>₹{total}</span>
          </div>

          <Link to="/checkout" className="btn-primary checkout-btn">
            Proceed to Checkout <ArrowRight size={18} />
          </Link>
          
          <Link to="/shop" className="continue-shopping">
            ← Continue Shopping
          </Link>

          <div className="summary-trust">
            <div className="trust-item">
              <span className="trust-dot" /> Secure Payment
            </div>
            <div className="trust-item">
              <span className="trust-dot" /> Fast Delivery
            </div>
            <div className="trust-item">
              <span className="trust-dot" /> 100% Organic
            </div>
            <div className="trust-item">
              <span className="trust-dot" /> Easy Returns
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
